// Featured cases are PLAYED through `gameAPI.js` on the compat routes, exactly
// like a generated case. This module is only the menu/archive surface that sits
// outside a game session: listing the cases and the admin cross-owner delete.
// Do not add gameplay calls here — they belong on `gameAPI`'s URL switch.
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const featuredAxios = axios.create({
  baseURL: API,
});

let getTokenFn = null;

export const setFeaturedCaseTokenGetter = (getter) => {
  getTokenFn = getter;
};

featuredAxios.interceptors.request.use(async (config) => {
  if (!getTokenFn || config.headers.Authorization) {
    return config;
  }

  let token = null;
  let lastError = null;

  for (let attempt = 0; attempt < 3; attempt += 1) {
    try {
      token = await getTokenFn();
      if (token) break;
    } catch (error) {
      lastError = error;
      console.warn(
        `[featuredCaseAPI] Token fetch failed (attempt ${attempt + 1}):`,
        error.message
      );
    }

    if (attempt < 2) {
      await new Promise((resolve) => setTimeout(resolve, 100 * (attempt + 1)));
    }
  }

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  } else {
    console.warn(
      '[featuredCaseAPI] No auth token available after retries',
      lastError?.message || 'token was null'
    );
  }

  return config;
}, (error) => Promise.reject(error));

featuredAxios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      error.isAuthError = true;

      if (typeof window !== 'undefined' && !window.__authSessionExpiredDispatched) {
        window.__authSessionExpiredDispatched = true;
        window.dispatchEvent(new CustomEvent('auth-session-expired', {
          detail: {
            message: 'Your session has expired. Please sign in again.',
            originalError: error,
          },
        }));
        setTimeout(() => {
          window.__authSessionExpiredDispatched = false;
        }, 5000);
      }
    }

    return Promise.reject(error);
  }
);

/** The featured-case list and per-player status behind the menu cards. */
export const listFeaturedCases = async () => {
  const response = await featuredAxios.get('/static-cases');
  return response.data;
};

/**
 * Admin cross-owner delete. Own-progress deletion goes through
 * `gameAPI.deleteGameSession`, which routes a `featured:{slug}` id onto the
 * compat archive route; only this one needs the `owner_id` parameter the
 * compat layer does not have.
 */
export const deleteFeaturedCase = async (slug, ownerId) => {
  const ownerQuery = ownerId ? `?owner_id=${encodeURIComponent(ownerId)}` : '';
  const response = await featuredAxios.delete(
    `/static-cases/${encodeURIComponent(slug)}${ownerQuery}`
  );
  return response.data;
};
